import { API_BASE_URL, getAuthToken, httpClient } from './core';

/** Wie eine Sidigo-Aktion bei Google und Meta heissen soll. */
export interface ConversionEventDto {
  enabled: boolean;
  /** Eventname für gtag/dataLayer bzw. den Google-Offline-Import. */
  googleEvent: string;
  /** Leer = keine Google-Ads-Conversion für diese Aktion. */
  googleAdsLabel: string;
  /** Meta-Standardevent (`Lead`, `Subscribe`, …) oder ein eigener Name. */
  metaEvent: string;
}

/** Katalogeintrag: was es überhaupt zu konfigurieren gibt. Kommt vom Server. */
export interface ConversionActionDto {
  key: string;
  label: string;
  description: string;
  /** `browser` meldet die Website selbst, `server` das Office. */
  source: 'browser' | 'server';
  defaultGoogleEvent: string;
  defaultMetaEvent: string;
}

export interface MarketingSettingsDto {
  enabled: boolean;
  gtmContainerId: string;
  /** GA4 Measurement-ID. Leer lassen, wenn GA4 im GTM-Container konfiguriert ist. */
  ga4MeasurementId: string;
  /** Google Ads Conversion-ID (AW-…). */
  googleAdsConversionId: string;
  /** Aktionsschlüssel → Event-Namen für Google und Meta. */
  conversionEvents: Record<string, ConversionEventDto>;
  /** Nur lesend: der Katalog der bekannten Aktionen. */
  conversionActions?: ConversionActionDto[];
  metaPixelId: string;
  metaCapiTestEventCode: string;
  /** Conversions zusätzlich serverseitig an die Meta CAPI senden. */
  serverEventsEnabled: boolean;
  attributionCookieDays: number;
  captureUtm: boolean;
  captureClickIds: boolean;
  captureReferrer: boolean;
  /**
   * Nur lesend: das CAPI-Token wird nie ausgeliefert. Beim Speichern bedeutet
   * ein leerer Wert "unverändert", `null` löscht das gespeicherte Token.
   */
  metaCapiAccessTokenIsSet?: boolean;
}

export type MarketingSettingsUpdateDto = Omit<
  MarketingSettingsDto,
  'metaCapiAccessTokenIsSet' | 'conversionActions'
> & {
  metaCapiAccessToken?: string | null;
};

/** Eine im Office ausgelöste Conversion (Probestunde konvertiert, Kurs angefangen). */
export interface MarketingConversionDto {
  id: number;
  action: string;
  actionLabel: string;
  occurredAt: string | null;
  subjectType: string;
  subjectId: number;
  /** Ohne Klick-ID kann Google die Conversion keinem Klick zuordnen. */
  hasGoogleClickId: boolean;
  metaEvent: string | null;
  metaSentAt: string | null;
  metaError: string | null;
  googleExportedAt: string | null;
  valueCents: number | null;
}

export interface MarketingConversionsResponseDto {
  items: MarketingConversionDto[];
  pendingExportCount: number;
}

export interface MarketingTestEventResultDto {
  ok: boolean;
  sent: boolean;
  status?: number;
  reason?: string;
  response?: string;
  /** Getestete Aktion und der Meta-Name, der dafür tatsächlich rausging. */
  action?: string;
  eventName?: string;
}

export interface FeatureSettingsDto {
  timeInterval: string;
  defaultView: string;
  enableContextMenu: boolean;
  allowRoomBooking: boolean;
  allowTrialLessons: boolean;
  maxBookingDays: string;
  subjectSingularLabel: string;
  subjectPluralLabel: string;
  googlePlacesApiKey: string;
  googlePlacesApiKeyIsSet?: boolean;
  // Website-Feature-Schalter.
  chatEnabled: boolean;
  loginEnabled: boolean;
}

export const marketingSettingsApi = {
  get: async (): Promise<MarketingSettingsDto> => {
    const res = await httpClient<MarketingSettingsDto>('/api/settings/marketing');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Marketing-Einstellungen');
    return res.data;
  },
  update: async (payload: MarketingSettingsUpdateDto): Promise<MarketingSettingsDto> => {
    const res = await httpClient<MarketingSettingsDto>('/api/settings/marketing', {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Speichern der Marketing-Einstellungen');
    return res.data;
  },
  /**
   * Sendet ein Test-Event an die Meta CAPI, um Pixel-ID + Token zu prüfen.
   * `action` ist ein Schlüssel aus dem Aktionskatalog; gesendet wird der dafür
   * konfigurierte Meta-Name.
   */
  sendTestEvent: async (payload?: { action?: string; email?: string; testEventCode?: string }) => {
    const res = await httpClient<MarketingTestEventResultDto>('/api/settings/marketing/test-event', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Test-Event konnte nicht gesendet werden');
    return res.data;
  },
  /** Im Office ausgelöste Conversions — zur Kontrolle und als Basis des Exports. */
  listConversions: async (includeExported = false): Promise<MarketingConversionsResponseDto> => {
    const res = await httpClient<MarketingConversionsResponseDto>(
      `/api/marketing/conversions?include_exported=${includeExported ? 'true' : 'false'}`,
    );
    if (res.error || !res.data) throw new Error(res.error || 'Conversions konnten nicht geladen werden');
    return res.data;
  },
  /**
   * CSV für den Google-Ads-Offline-Import. Kommt als Datei, nicht als JSON —
   * deshalb `fetch` statt `httpClient`.
   *
   * `mark = false` liefert dieselben Zeilen zur Ansicht, ohne sie als
   * exportiert zu stempeln.
   */
  downloadConversionExport: async (mark = true): Promise<{ blob: Blob; filename: string }> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(
      `${API_BASE_URL}/api/marketing/conversions/export?mark=${mark ? 'true' : 'false'}`,
      { method: 'GET', credentials: 'include', headers },
    );
    if (!res.ok) throw new Error(`Export fehlgeschlagen (HTTP ${res.status})`);
    const disposition = res.headers.get('content-disposition') || '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await res.blob(), filename: match?.[1] || 'google-ads-conversions.csv' };
  },
};

export const featureSettingsApi = {
  get: async (): Promise<FeatureSettingsDto> => {
    const res = await httpClient<FeatureSettingsDto>('/api/settings/features');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Funktionseinstellungen');
    return res.data;
  },
  update: async (payload: FeatureSettingsDto): Promise<FeatureSettingsDto> => {
    const res = await httpClient<FeatureSettingsDto>('/api/settings/features', {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Speichern der Funktionseinstellungen');
    return res.data;
  },
};

